'use client';
import { analytics } from '@lib/analytics/page-events';
import { signIn } from '@lib/auth';
import { useMicrocopy } from '@lib/cms/microcopy/client';
import { ACTIVE_ORGANIZATION_ID_COOKIE } from '@lib/constants/cookies';
import { useFlags } from '@lib/flags/amplitude/client';
import { FEATURE_FLAG_ON, Flags } from '@lib/flags/flagConstants';
import { failingFetcher } from '@lib/http/fetch';
import { Organization } from '@lib/types/api/Organization';
import {
  GetProductListsV2Response,
  LookupProductMembershipResponse,
  ProductListV2,
} from '@lib/types/api/ProductListV2';
import { debounce } from '@lib/util/debounce';
import Cookies from 'js-cookie';
import { useSession } from 'next-auth/react';
import {
  PropsWithChildren,
  useCallback,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import useSWR, { useSWRConfig } from 'swr';
import {
  ChangeFavoriteListModal,
  FavoriteListModal,
  SnackBar,
  type FavoriteListModalFormData,
  type ListData,
} from 'ui-components';

import { ProductListsContext } from './ProductListsContext';
import {
  addItemToList,
  createList,
  removeItemFromList,
} from './productListsApi';
import {
  buildSnackbarData,
  getListChanges,
  maybePromoteDefaultList,
  syncListChanges,
  toListData,
  trackListChangesAnalytics,
  type ProductInfo,
  type SnackbarData,
} from './productListsHelpers';
import {
  PRODUCT_LISTS_MICROCOPY_DEFAULTS,
  buildChangeModalMicrocopy,
  buildCreateModalMicrocopy,
} from './productListsMicrocopy';
import { INITIAL_MODAL_FLOW, modalFlowReducer } from './productListsReducer';
import { useProductMembership } from './useProductMembership';

export const ProductListsProvider = ({ children }: PropsWithChildren) => {
  const { flags } = useFlags();
  const isProductListsEnabled =
    flags?.[Flags.PRODUCT_LISTS]?.value === FEATURE_FLAG_ON;
  const { status, data: session } = useSession();
  const isAuthenticated = status === 'authenticated';
  const shouldFetch = isAuthenticated && isProductListsEnabled;

  const { data: orgData } = useSWR<{
    organizations: Organization[];
    defaultOrganizationId: string;
  }>(shouldFetch ? '/api/users/organizations' : null, failingFetcher);

  const activeOrganization = useMemo(() => {
    const organizations = orgData?.organizations ?? [];
    if (organizations.length === 0) return null;
    const storedOrgId = Cookies.get(ACTIVE_ORGANIZATION_ID_COOKIE);
    return (
      organizations.find((org) => org.id === storedOrgId) ??
      organizations.find((org) => org.id === orgData?.defaultOrganizationId) ??
      organizations[0]
    );
  }, [orgData]);

  const organizationId = activeOrganization?.id;

  const [modalFlow, dispatch] = useReducer(
    modalFlowReducer,
    INITIAL_MODAL_FLOW
  );
  const modalFlowRef = useRef(modalFlow);
  modalFlowRef.current = modalFlow;

  const [snackbar, setSnackbar] = useState<SnackbarData | null>(null);
  const snackbarRef = useRef(snackbar);
  snackbarRef.current = snackbar;

  const [serverError, setServerError] = useState(false);

  const safeDispatch = useCallback((action: any) => {
    setServerError(false);
    dispatch(action);
  }, []);

  const {
    isProductInList,
    fetchProductMembership,
    fetchBatchProductMembership,
    addToListOptimistic,
    removeFromListOptimistic,
    rollbackMembership,
    setMembership,
    getMembership,
    buildLookupUrl,
  } = useProductMembership(shouldFetch, organizationId);

  const mc = useMicrocopy('product-lists', PRODUCT_LISTS_MICROCOPY_DEFAULTS);

  const { mutate: globalMutate } = useSWRConfig();

  // This is mainly used to reactively refresh data at AccountsListsPage or UserListPage
  const invalidateListItems = useCallback(
    (listId: string) => {
      globalMutate(
        (key) =>
          typeof key === 'string' &&
          (key.startsWith(`/api/lists/${listId}/items`) ||
            (organizationId &&
              key.startsWith(
                `/api/organizations/${organizationId}/lists/${listId}/items`
              )))
      );
    },
    [globalMutate, organizationId]
  );

  // Fetch user's personal lists
  const { data: listsData, mutate: refreshLists } =
    useSWR<GetProductListsV2Response>(
      shouldFetch ? '/api/lists' : null,
      failingFetcher
    );

  const personalLists = useMemo(() => listsData ?? [], [listsData]);

  // Fetch organization lists
  const { data: orgListsData, mutate: refreshOrgLists } =
    useSWR<GetProductListsV2Response>(
      shouldFetch && organizationId
        ? `/api/organizations/${organizationId}/lists`
        : null,
      failingFetcher
    );

  const orgLists = useMemo(() => orgListsData ?? [], [orgListsData]);
  const orgListIds = useMemo(
    () => new Set(orgLists.map((l) => l.id)),
    [orgLists]
  );

  const defaultList = useMemo(
    () =>
      personalLists.find((list) => list.isFavorites) ??
      // These other values are never reached in theory because user
      // always have a Favorite List (is not removable)
      personalLists[0] ??
      null,
    [personalLists]
  );

  // -- Add / Remove callbacks ----------------------------------------------

  const addToList = useCallback(
    async (product: ProductInfo, list: ProductListV2) => {
      const prevMembership = getMembership(product.id);
      const isOrgList = orgListIds.has(list.id);
      addToListOptimistic(product.id, list.id, isOrgList);

      try {
        await addItemToList(list.id, product.id);

        analytics.pushSaveToListEvent({
          asset_id: product.id,
          asset_name: product.name,
        });

        setSnackbar({
          type: 'added',
          listName: list.name,
          listSlug: list.slug,
          productId: product.id,
          productName: product.name,
          key: Date.now(),
        });
        await refreshLists();
        invalidateListItems(list.id);
      } catch (error) {
        rollbackMembership(product.id, prevMembership);
        throw error;
      }
    },
    [
      getMembership,
      orgListIds,
      addToListOptimistic,
      rollbackMembership,
      refreshLists,
      invalidateListItems,
    ]
  );

  const removeFromList = useCallback(
    async (product: ProductInfo, list: ProductListV2) => {
      const prevMembership = getMembership(product.id);
      const isOrgList = orgListIds.has(list.id);
      removeFromListOptimistic(product.id, list.id, isOrgList);

      try {
        await removeItemFromList(list.id, product.id);

        analytics.pushRemoveFromListEvent({
          asset_id: product.id,
          asset_name: product.name,
        });

        setSnackbar({
          type: 'removed',
          listName: list.name,
          listSlug: list.slug,
          productId: product.id,
          productName: product.name,
          key: Date.now(),
        });
        // These are needed in pages like Account Lists page and User List Page
        await refreshLists();
        invalidateListItems(list.id);
      } catch (error) {
        rollbackMembership(product.id, prevMembership);
        throw error;
      }
    },
    [
      getMembership,
      orgListIds,
      removeFromListOptimistic,
      rollbackMembership,
      refreshLists,
      invalidateListItems,
    ]
  );

  // -- Ref for debounced toggle --
  const latestRef = useRef({
    isAuthenticated,
    defaultList,
    personalLists,
    orgLists,
    orgListIds,
    addToList,
    removeFromList,
    fetchProductMembership,
  });
  latestRef.current = {
    isAuthenticated,
    defaultList,
    personalLists,
    orgLists,
    orgListIds,
    addToList,
    removeFromList,
    fetchProductMembership,
  };

  const toggleProductList = useMemo(
    () =>
      debounce(async (product: ProductInfo) => {
        if (!latestRef.current.isAuthenticated) {
          signIn();
          return;
        }

        try {
          const productMembership =
            await latestRef.current.fetchProductMembership(product.id);

          const allListIds = [
            ...productMembership.personal,
            ...productMembership.organization,
          ];

          const { personal: personalMembership } = productMembership;

          const isInPersonalList = personalMembership.length > 0;
          if (!isInPersonalList) {
            latestRef.current.addToList(product, latestRef.current.defaultList);
            return;
          }

          const isOnlyInOnePersonalList = personalMembership.length === 1;
          if (isOnlyInOnePersonalList) {
            latestRef.current.removeFromList(
              product,
              latestRef.current.personalLists.find(
                (l) => l.id === personalMembership[0]
              ) as ProductListV2
            );
            return;
          }

          // User needs to select a list to remove from
          dispatch({
            type: 'OPEN_CHANGE_MODAL',
            productId: product.id,
            productName: product.name,
            initialSelectedListIds: allListIds,
            intent: 'remove',
            allowDefaultPromotion: true,
          });
        } catch (error) {
          console.error('Error toggling product in list:', error);
        }
      }, 300),
    []
  );

  // -- Modal action callbacks ----------------------------------------------
  const openCreateListModal = useCallback(() => {
    const current = snackbarRef.current;
    if (!current?.productId) return;
    dispatch({
      type: 'OPEN_CREATE_MODAL',
      productId: current.productId,
      productName: current.productName,
      intent: 'add',
      allowDefaultPromotion: true,
    });
  }, []);

  const openChangeListModal = useCallback(async (product: ProductInfo) => {
    const membership = await latestRef.current.fetchProductMembership(
      product.id
    );
    dispatch({
      type: 'OPEN_CHANGE_MODAL',
      productId: product.id,
      productName: product.name,
      initialSelectedListIds: [
        ...membership.personal,
        ...membership.organization,
      ],
      intent: 'add',
      allowDefaultPromotion: true,
    });
  }, []);

  const createListAndAddProduct = useCallback(
    async (listFormData: FavoriteListModalFormData) => {
      const currentModalFlow = modalFlowRef.current;

      if (currentModalFlow.step !== 'create-list') return;

      const { productId, productName, intent, allowDefaultPromotion } =
        currentModalFlow;

      const isOrgList = listFormData.accountType === 'organization';
      const targetOrgId = isOrgList ? organizationId : undefined;
      setServerError(false);

      dispatch({ type: 'SET_SUBMITTING', isSubmitting: true });
      try {
        const newList = await createList(
          listFormData.listName,
          listFormData.visibility,
          targetOrgId
        );
        analytics.pushCreateListEvent({
          list_id: newList.id,
          list_name: listFormData.listName,
          list_visibility: listFormData.visibility,
          list_type: isOrgList ? 'organization' : 'personal',
          product_id: productId,
          product_name: productName,
          organization_id: targetOrgId,
        });

        await addItemToList(newList.id, productId, targetOrgId);
        analytics.pushSaveToListEvent({
          asset_id: productId,
          asset_name: productName,
        });

        if (isOrgList) {
          await refreshOrgLists();
        } else {
          await refreshLists();
        }
        invalidateListItems(newList.id);

        const lookupUrl = buildLookupUrl(encodeURIComponent(productId));
        const lookupData = (await failingFetcher(
          lookupUrl
        )) as LookupProductMembershipResponse;
        const membership = lookupData.products[productId] ?? {
          personal: [],
          organization: [],
        };

        // Ensure the new list is in the right bucket —
        // sometimes the backend lookup doesn't return the new list right after creation.
        const bucket = isOrgList ? 'organization' : 'personal';
        if (!membership[bucket].includes(newList.id)) {
          membership[bucket] = [...membership[bucket], newList.id];
        }

        setMembership(productId, membership);
        const initialSelectedListIds = [
          ...membership.personal,
          ...membership.organization,
        ];

        safeDispatch({
          type: 'CREATE_LIST_SUCCESS',
          productId,
          productName,
          newListId: newList.id,
          newListName: newList.name,
          newListSlug: newList.slug,
          initialSelectedListIds,
          intent,
          allowDefaultPromotion,
        });
      } catch (error) {
        setServerError(true);
        console.error('Error creating list and adding product:', error);
        dispatch({ type: 'SET_SUBMITTING', isSubmitting: false });
        throw error;
      }
    },
    [
      organizationId,
      refreshLists,
      refreshOrgLists,
      invalidateListItems,
      buildLookupUrl,
      setMembership,
      safeDispatch,
    ]
  );

  const updateProductListMembership = useCallback(
    async (selectedLists: ListData[]) => {
      if (modalFlowRef.current.step !== 'change-list') return;
      setServerError(false);
      try {
        dispatch({ type: 'SET_SUBMITTING', isSubmitting: true });

        const { productId, productName, initialSelectedListIds } =
          modalFlowRef.current;
        const { listsToAdd, idsToRemove } = getListChanges(
          initialSelectedListIds,
          selectedLists
        );
        const { orgListIds } = latestRef.current;
        const getOrgIdForList = (listId: string) =>
          orgListIds.has(listId) ? organizationId : undefined;

        await syncListChanges({
          listsToAdd,
          idsToRemove,
          productId,
          getOrgIdForList,
        });
        trackListChangesAnalytics({
          listsToAdd,
          idsToRemove,
          productId,
          productName,
        });

        await maybePromoteDefaultList({
          selectedPersonalLists: selectedLists.filter((l) => l.personal),
          allowDefaultPromotion: !!modalFlowRef.current.allowDefaultPromotion,
          currentDefaultListId: latestRef.current.defaultList?.id,
        });

        // Refresh caches
        const addedIds = listsToAdd.map((l) => l.id);
        const hasOrgChanges = [...addedIds, ...idsToRemove].some((id) =>
          orgListIds.has(id)
        );
        await refreshLists();
        if (hasOrgChanges) await refreshOrgLists();
        [...addedIds, ...idsToRemove].forEach(invalidateListItems);

        setMembership(productId, {
          personal: selectedLists.filter((l) => l.personal).map((l) => l.id),
          organization: selectedLists
            .filter((l) => !l.personal)
            .map((l) => l.id),
        });

        const snackbarData = buildSnackbarData({
          listsToAdd,
          idsToRemove,
          productId,
          productName,
          createdList: modalFlowRef.current.createdList ?? null,
          intent: modalFlowRef.current.intent,
          allLists: [
            ...latestRef.current.personalLists,
            ...latestRef.current.orgLists,
          ],
          multipleListsLabel: mc['product-lists.multiple-lists'],
        });
        if (snackbarData) setSnackbar(snackbarData);

        safeDispatch({ type: 'CHANGE_LIST_SUCCESS' });
      } catch (error) {
        setServerError(true);
        console.error('Error syncing list changes:', error);
        dispatch({ type: 'SET_SUBMITTING', isSubmitting: false });
        throw error;
      }
    },
    [
      organizationId,
      refreshLists,
      refreshOrgLists,
      invalidateListItems,
      setMembership,
      mc,
      safeDispatch,
    ]
  );

  const handleChangeListSave = useCallback(
    async (selectedLists: ListData[]) => {
      if (modalFlowRef.current.step !== 'change-list') return;

      const currentModalFlow = modalFlowRef.current;

      const { listsToAdd, idsToRemove } = getListChanges(
        currentModalFlow.initialSelectedListIds,
        selectedLists
      );

      if (listsToAdd.length > 0 || idsToRemove.length > 0) {
        await updateProductListMembership(selectedLists);
        return;
      }

      // Create-flow confirmation — product was already added, just show snackbar
      if (currentModalFlow.fromCreateFlow && currentModalFlow.createdList) {
        const {
          createdList: { name: listName, slug: listSlug },
          productId,
          productName,
        } = currentModalFlow;
        setSnackbar({
          type: 'added',
          listName,
          listSlug: listSlug ?? '',
          productId,
          productName,
          key: Date.now(),
        });
      }
      dispatch({ type: 'CHANGE_LIST_DISMISS' });
    },
    [updateProductListMembership]
  );

  const openNewListModal = useCallback(() => {
    dispatch({ type: 'NEW_LIST_FOR_PRODUCT_REQUESTED' });
  }, []);

  const createModalMicrocopy = useMemo(
    () => buildCreateModalMicrocopy(mc),
    [mc]
  );

  const isFromCreateFlow =
    modalFlow.step === 'change-list' && modalFlow.fromCreateFlow;

  const changeModalMicrocopy = useMemo(
    () => buildChangeModalMicrocopy(mc, !!isFromCreateFlow),
    [mc, isFromCreateFlow]
  );

  const closeSnackbar = useCallback(() => {
    setSnackbar((current) =>
      current?.key === snackbarRef.current?.key ? null : current
    );
  }, []);

  const isSubmitting =
    modalFlow.step !== 'idle' ? modalFlow.isSubmitting : false;

  const snackbarProps = useMemo(() => {
    if (!snackbar) return null;

    const hasMultipleLists = personalLists.length + orgLists.length > 1;

    if (snackbar.type === 'removed') {
      return {
        iconWeight: 'bold' as const,
        closeIcon: 'X',
        microcopy: {
          title: mc['product-lists.removed-from'],
          actionLabel: snackbar.listName,
          closeAriaLabel: mc['product-lists.close'],
        },
      };
    }

    return {
      iconWeight: 'fill' as const,
      ...(snackbar.listSlug
        ? {
            actionIcon: 'ArrowSquareOut',
            onActionClick: () =>
              window.open(`/lists/${snackbar.listSlug}`, '_blank'),
          }
        : {}),
      closeIcon: hasMultipleLists ? 'CaretDown' : 'Plus',
      microcopy: {
        title: mc['product-lists.added-to'],
        ...(snackbar.listSlug
          ? { actionAriaLabel: mc['product-lists.view-list'] }
          : {}),
        actionLabel: snackbar.listName,
        closeAriaLabel: hasMultipleLists
          ? mc['product-lists.change-list']
          : mc['product-lists.add-to-new-list'],
      },
      onCloseIconClick: hasMultipleLists
        ? () =>
            openChangeListModal({
              id: snackbar.productId,
              name: snackbar.productName,
            })
        : openCreateListModal,
    };
  }, [
    snackbar,
    personalLists.length,
    orgLists.length,
    mc,
    openCreateListModal,
    openChangeListModal,
  ]);

  const value = useMemo(
    () => ({
      personalLists,
      orgLists,
      defaultList,
      isLoading: !listsData && isAuthenticated,
      isProductInList,
      toggleProductList,
      openCreateListModal,
      onChangeList: openChangeListModal,
      fetchProductMembership,
      fetchBatchProductMembership,
    }),
    [
      personalLists,
      orgLists,
      defaultList,
      listsData,
      isAuthenticated,
      isProductInList,
      toggleProductList,
      openCreateListModal,
      openChangeListModal,
      fetchProductMembership,
      fetchBatchProductMembership,
    ]
  );

  return (
    <ProductListsContext.Provider value={value}>
      {children}
      {snackbarProps && snackbar && (
        <SnackBar
          key={snackbar.key}
          icon="BookmarkSimple"
          onClose={closeSnackbar}
          {...snackbarProps}
        />
      )}
      <FavoriteListModal
        open={modalFlow.step === 'create-list'}
        onOpenChange={(open) => {
          if (!open) dispatch({ type: 'MODAL_DISMISSED' });
        }}
        listName=""
        visibility="private"
        accountType="personal"
        showAccountType={!!organizationId}
        userName={session?.user?.name ?? ''}
        userProfilePicture={session?.user?.image}
        publisherName={activeOrganization?.name ?? ''}
        microcopy={createModalMicrocopy}
        onSave={createListAndAddProduct}
        isSubmitting={isSubmitting}
        position="bottom"
        showBackdrop={false}
        serverError={serverError}
        onDismissError={() => setServerError(false)}
      />
      {modalFlow.step === 'change-list' && (
        <ChangeFavoriteListModal
          open
          onOpenChange={(open) => {
            if (!open) dispatch({ type: 'MODAL_DISMISSED' });
          }}
          list={[
            ...personalLists.map((l) => toListData(l, true)),
            ...orgLists.map((l) => toListData(l, false)),
          ]}
          multiTabs={!!organizationId}
          initialSelectedListIds={modalFlow.initialSelectedListIds}
          forceEnableSave={!!isFromCreateFlow}
          userName={session?.user?.name ?? ''}
          userProfilePicture={session?.user?.image}
          publisherName={activeOrganization?.name ?? ''}
          microcopy={changeModalMicrocopy}
          onNewList={openNewListModal}
          onSave={handleChangeListSave}
          isSubmitting={isSubmitting}
          position="bottom"
          showBackdrop={false}
          serverError={serverError}
          onDismissError={() => setServerError(false)}
        />
      )}
    </ProductListsContext.Provider>
  );
};
